import Image from 'next/image'
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";
import Link from "next/link";

interface DiscoverProcedureData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  video?: {
    id: string;
    filename_download: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          // site_content
          title: string;
          text: string;
          // menu_items
          href: string;
          label: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  "subheading.text",
  "subheading.title",

  // button
  "button.label",
  "button.href",

  // video
  "video.id",
  "video.filename_download",

  // content collection
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
  "content_collection.site_content_collections_id.collection.item:menu_items.href",
  "content_collection.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface DiscoverProcedureProps {
  data?: DiscoverProcedureData;
}

export default function DiscoverProcedure({ data }: DiscoverProcedureProps) {
  return (
    <section className="relative w-full overflow-hidden bg-[#94a4bb]">
      {/* Background Video Container */}
      <div className="absolute z-10 inset-0 hidden bg-primary/50 lg:block">
        {data?.video && (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="h-full w-full object-cover object-center"
          >
            <source src={`${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${data.video.id}`} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-primary/50 xl:bg-primary/30 z-10" />
      </div>

      {/* Content Container */}
      <div className="relative z-20 mx-auto h-full px-8 xl:px-16 py-8 xl:py-16 lg:container">
        {/* Header Section */}
        <div className="mx-auto mb-4 max-w-4xl text-center lg:mb-16">
          <h2 className="text-[2rem] font-essonnes font-light leading-tight text-left md:text-center md:text-5xl lg:text-[3.375rem] mb-4 text-white lg:mb-8">
            {data?.heading?.title}
          </h2>
          <div 
            className="font-greycliff text-[1rem] font-normal leading-normal text-left md:text-center lg:text-lg lg:leading-normal text-white xl:text-xl"
            dangerouslySetInnerHTML={sanitizeHTML(data?.subheading?.text || "")}
          />
          
        </div>

        {/* Procedures Grid */}
        <div className={`flex overflow-x-auto bader-h-scrollbar-white pb-8 gap-4 lg:gap-8 ${data?.content_collection && data.content_collection.length <= 4 ? 'md:justify-center md:flex-wrap' : ''}`}>
          {data?.content_collection?.map((collection, index) => {
            const titleItem = collection?.site_content_collections_id?.collection.find(
              item => item.item.title
            )?.item;
            
            const textItem = collection?.site_content_collections_id?.collection.find(
              item => item.item.text
            )?.item;
            
            const menuItem = collection?.site_content_collections_id?.collection.find(
              item => item.collection === "menu_items"
            )?.item;

            return (
              <div key={index} className="group flex-none shrink-0 w-full md:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)] relative rounded-[1.25rem] bg-white/60 transition-all duration-300 hover:bg-white/70">
                <div className="flex items-center justify-between p-8 h-full">
                  <div className="flex-1 self-start flex flex-col h-full">
                    <h3 className="mb-4 font-essonnes text-3xl xl:text-4xl font-light leading-[1.2] text-primary lg:mb-6">
                      {titleItem?.title}
                    </h3>
                    <div className="font-greycliff text-primary md:text-lg text-[1rem] font-normal leading-normal [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-lg" dangerouslySetInnerHTML={sanitizeHTML(textItem?.text || "")} />
                    <div className="mt-auto pt-4">
                      {menuItem?.href && menuItem?.label && (
                        <Link 
                          href={menuItem.href}
                          className="inline-block px-6 py-3.5 border border-primary rounded-full text-primary text-xl font-medium font-greycliff group-hover:text-primary group-hover:border-primary"
                        >
                          {menuItem.label}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="hidden lg:block mt-16 text-center">
          {data?.button?.label && data?.button?.href && (
            <Link 
              href={data.button.href} 
              className="rounded-[3.125rem] bg-primary px-9 py-4 font-greycliff text-xl text-white transition-all duration-300 hover:bg-light hover:text-primary inline-block"
            >
              {data.button.label}
            </Link>
          )}
        </div>
      </div>
    </section>
  )
} 